import React, { useState, useEffect, useContext, createContext } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useAuth } from '../hooks/useAuth'
import { useProgress } from '../hooks/useProgress'
import { QuizEngine } from '../components/quiz/QuizEngine'
import { parseLesson, getAdjacentLessons } from '../lib/content'
import { stripEmoji, calloutLabel, parseScenarioLines } from '../lib/lessonRenderer'
import { LESSON_INDEX } from '../content/lessonIndex'
import { supabase } from '../lib/supabase'
import { Clock, ChevronLeft, ChevronRight, CheckCircle, BookOpen } from 'lucide-react'
import './LessonPage.css'

// Context used only by capstone lessons so DecisionBlock children can
// report their answered state upward without prop-drilling through
// SectionRenderer. For normal lessons this context is null and
// DecisionBlock falls back to local-only state.
const CapstoneDPContext = createContext(null)

export function LessonPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { profile, loading: authLoading } = useAuth()
  const { isLessonComplete, markLessonComplete, completeCapstone, progress } = useProgress()
  const [lesson, setLesson] = useState(null)
  const [adjacent, setAdjacent] = useState({ prev: null, next: null })
  const [completed, setCompleted] = useState(false)
  const [notFound, setNotFound] = useState(false)

  // Capstone DP tracking — which DP indices have been answered (set of numbers)
  const [answeredDPs, setAnsweredDPs] = useState(new Set())

  // Capstone completion state
  const [issuing, setIssuing] = useState(false)
  const [issueError, setIssueError] = useState(null)

  // ── Subscription paywall guard ─────────────────────────────────────────
  // TechBench is paid from the first lesson — no free Foundation tier.
  // The universe map's ModulePanel intercepts lesson clicks for unpaid
  // users and shows the PaywallModal in-place, so the normal flow never
  // lands here. This guard catches deep links, bookmarks, browser-back
  // into /learn/* without an active subscription, or a subscription
  // that expired between page load and now. Send them back to the
  // dashboard; the modal fires on the next lesson click from there.
  useEffect(() => {
    if (authLoading) return
    if (!profile) return
    if (profile.subscription_status !== 'active') {
      navigate('/dashboard', { replace: true })
    }
  }, [profile, authLoading, navigate])

  // ── Lesson content fetch ───────────────────────────────────────────────
  // Content is served by the get-lesson function, which checks the JWT and
  // the subscription/tier before returning the markdown. The raw lesson text
  // is never in the front-end bundle. While the request is in flight `lesson`
  // is null, so the existing spinner shows.
  //
  // A 401/403 means the session or subscription doesn't grant access — we
  // send the user back to the dashboard, exactly like the paywall guard
  // above. A 404 shows the "not published yet" state. This effect depends
  // only on `slug`, so navigating between lessons refetches, but completing a
  // lesson (which changes progress) does NOT trigger a refetch.
  useEffect(() => {
    let cancelled = false
    window.scrollTo({ top: 0 })
    setLesson(null)
    setNotFound(false)
    setAnsweredDPs(new Set())
    setIssueError(null)

    async function load() {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) {
        if (!cancelled) navigate('/dashboard', { replace: true })
        return
      }

      let res
      try {
        res = await fetch(
          `/.netlify/functions/get-lesson?slug=${encodeURIComponent(slug)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      } catch (err) {
        if (!cancelled) setNotFound(true)
        return
      }
      if (cancelled) return

      if (res.status === 401 || res.status === 403) {
        navigate('/dashboard', { replace: true })
        return
      }
      if (!res.ok) { setNotFound(true); return }

      const rawStr = await res.text()
      if (cancelled) return

      const parsed = parseLesson(rawStr)
      setLesson(parsed)
      const mainLessons = LESSON_INDEX.filter(l => !l.type || l.type === 'ref')
      setAdjacent(getAdjacentLessons(mainLessons, slug))
    }

    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  // Keep the "complete" badge in sync without refetching the lesson. Runs
  // when the lesson loads and whenever progress changes (e.g. right after the
  // user passes the quiz).
  useEffect(() => {
    if (lesson?.meta?.id) setCompleted(isLessonComplete(lesson.meta.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson, progress.completedLessons])

  // Helper: handle the subscription_required race — if the subscription
  // expired between page load and submit, the RPC returns this code and
  // we redirect rather than silently swallowing the failure.
  function handleRpcError(error) {
    // Mid-session race: subscription expired between page load and submit,
    // or T2+ content with a tier downgrade (which is impossible by design
    // but defensive against bad lesson IDs). Send back to the map;
    // ModulePanel will surface the paywall modal on the next click.
    if (error?.code === 'subscription_required') {
      navigate('/dashboard', { replace: true })
      return true
    }
    if (error?.code === 'tier_insufficient') {
      navigate('/dashboard', { replace: true })
      return true
    }
    return false
  }

  async function handlePass(score) {
    if (!lesson?.meta?.id) return
    const { error } = await markLessonComplete(lesson.meta.id, score)
    if (error) {
      if (handleRpcError(error)) return
      // Other errors — surface to user via a console for now; the lesson
      // page UX doesn't currently have an error toast, so we keep the
      // user on the page rather than navigating away.
      console.error('Failed to mark lesson complete:', error)
      return
    }
    // Navigate directly to next lesson — no intermediate state
    if (adjacent.next) {
      navigate(`/learn/${adjacent.next.slug}`)
    } else {
      setCompleted(true)
    }
  }

  async function handleMarkComplete() {
    if (!lesson?.meta?.id) return
    const { error } = await markLessonComplete(lesson.meta.id, null)
    if (error) {
      if (handleRpcError(error)) return
      console.error('Failed to mark lesson complete:', error)
      return
    }
    if (adjacent.next) {
      navigate(`/learn/${adjacent.next.slug}`)
    } else {
      setCompleted(true)
    }
  }

  // Capstone: called by the CTA at the bottom of the lesson.
  // Marks the lesson complete locally AND calls completeCapstone() which
  // writes to Supabase and mints the credential via RPC. On success,
  // navigate to the certificate page.
  async function handleCompleteCapstone() {
    if (!lesson?.meta?.id) return
    if (issuing) return
    setIssuing(true)
    setIssueError(null)

    // Mark the capstone lesson complete in progress.completed_lessons first
    const { error: markErr } = await markLessonComplete(lesson.meta.id, null)
    if (markErr) {
      if (handleRpcError(markErr)) return
      setIssueError(markErr.message || 'Could not record capstone completion.')
      setIssuing(false)
      return
    }

    // Issue the credential (calls pass_foundation_capstone + issue_foundation_credential)
    const { error, credentialId } = await completeCapstone()

    if (error) {
      if (handleRpcError(error)) return
      setIssueError(error.message || 'Something went wrong issuing your certificate.')
      setIssuing(false)
      return
    }

    // On success: navigate to the certificate page, flagging it as newly issued
    // so the page can show a celebratory state on arrival.
    navigate(`/certificate?new=1&id=${encodeURIComponent(credentialId || '')}`)
  }

  if (notFound) return (
    <div className="lesson-404">
      <h1>Lesson not found</h1>
      <p>This lesson hasn't been published yet.</p>
      <Link to="/learn" className="btn btn--secondary btn--md">Back to course</Link>
    </div>
  )

  if (!lesson) return (
    <div className="lesson-loading"><div className="lesson-loading__spinner" /></div>
  )

  const { meta, body, quiz } = lesson
  const isIntro    = meta.type === 'intro' || meta.position === 0
  const isRef      = meta.type === 'ref' || slug.includes('-ref-')
  const isHS       = meta.id?.startsWith('hs-') || slug.startsWith('hs-')
  const isCapstone = meta.type === 'capstone'
  const trackLabel = isHS ? 'Human Skills' : 'IT Foundation'
  const sections   = parseSections(body)

  // Count total decision points in this lesson (used for capstone CTA gating)
  const totalDPs = sections.filter(s => s.type === 'decision').length
  const allDPsAnswered = totalDPs > 0 && answeredDPs.size >= totalDPs

  // Callback passed through context so each DecisionBlock can report its answer
  const reportDPAnswered = (dpIndex) => {
    setAnsweredDPs(prev => {
      if (prev.has(dpIndex)) return prev
      const next = new Set(prev)
      next.add(dpIndex)
      return next
    })
  }

  // Has the user already earned the Foundation credential? If so, the capstone
  // becomes a read-only experience with a link to their existing cert.
  const alreadyEarned = isCapstone && progress.credentialId

  return (
    <CapstoneDPContext.Provider value={isCapstone ? { reportDPAnswered } : null}>
    <article className={`lesson ${isCapstone ? 'lesson--capstone' : ''}`}>
      <nav className="lesson__breadcrumb">
        <Link to="/learn" className="lesson__breadcrumb-link">
          <ChevronLeft size={14} />Course
        </Link>
        <span className="lesson__breadcrumb-sep">/</span>
        <span>{isCapstone ? 'Foundation Capstone' : trackLabel}</span>
        {!isCapstone && meta.module > 0 && <><span className="lesson__breadcrumb-sep">/</span><span>Module {meta.module}</span></>}
      </nav>

      {/* Badges suppressed on capstone — its hero handles status display */}
      {!isCapstone && (
        <div className="lesson__badges">
          <span className={`badge ${isHS ? 'badge--hs' : 'badge--tier'}`}>
            {isHS ? 'Human Skills' : `Tier ${meta.tier} · IT Foundation`}
          </span>
          {isRef && (
            <span className="badge badge--ref"><BookOpen size={11} />Reference</span>
          )}
          {meta.time_range && meta.time_range !== '—' && !isRef && (
            <span className="badge badge--time"><Clock size={11} />{meta.time_range}</span>
          )}
          {completed && !isRef && (
            <span className="badge badge--done"><CheckCircle size={11} />Complete</span>
          )}
        </div>
      )}

      {/* On capstones, only show the "certificate earned" badge (smaller surface) */}
      {isCapstone && alreadyEarned && (
        <div className="lesson__badges">
          <span className="badge badge--done"><CheckCircle size={11} />Certificate earned</span>
        </div>
      )}

      {/* Title/subtitle suppressed on capstone — its hero owns these */}
      {!isCapstone && (
        <>
          <h1 className="lesson__title">{stripEmoji(meta.title)}</h1>
          {meta.subtitle && <p className="lesson__subtitle">{stripEmoji(meta.subtitle)}</p>}
        </>
      )}

      {/* Body — capstone gets its dedicated render, everything else uses sections */}
      {isCapstone ? (
        <CapstoneRender sections={sections} />
      ) : (
        <div className="lesson__body">
          {sections.map((section, i) => (
            <SectionRenderer key={i} section={section} />
          ))}
        </div>
      )}

      {/* Ref pages: just a back link, no quiz */}
      {isRef && (
        <div className="lesson__ref-footer">
          <Link to="/learn" className="btn-ref-back">
            <ChevronLeft size={14} />Back to lesson list
          </Link>
        </div>
      )}

      {/* Capstone CTA — replaces the normal mark-complete / quiz footer.
          Disabled until all decision points have been answered. If the
          user has already earned the credential, show a "view your
          certificate" link instead. */}
      {isCapstone && (
        <div className="capstone-cta">
          {alreadyEarned ? (
            <>
              <div className="capstone-cta__eyebrow mono">Foundation complete</div>
              <h2 className="capstone-cta__title">You've earned this one.</h2>
              <p className="capstone-cta__sub">
                Your IT Foundation Ready certificate is already issued. Open it any time.
              </p>
              <Link to="/certificate" className="capstone-cta__btn">
                View your certificate
              </Link>
            </>
          ) : (
            <>
              <div className="capstone-cta__eyebrow mono">Ready to complete Foundation</div>
              <h2 className="capstone-cta__title">You built something real.</h2>
              <p className="capstone-cta__sub">
                The technical foundation. The human skills. The AI literacy. All three tracks, brought
                together in the one situation that actually matters: doing the work.
              </p>
              <div className="capstone-cta__status mono">
                {totalDPs > 0
                  ? `${answeredDPs.size} of ${totalDPs} decision points answered`
                  : 'Work through the scenario to continue'}
              </div>
              <button
                className="capstone-cta__btn"
                onClick={handleCompleteCapstone}
                disabled={!allDPsAnswered || issuing}
              >
                {issuing ? 'Issuing your certificate…' : 'Complete Foundation'}
              </button>
              {issueError && (
                <div className="capstone-cta__error mono">
                  {issueError}
                </div>
              )}
              <p className="capstone-cta__hint mono">
                Your IT Foundation Ready certificate will be issued to your profile.
              </p>
            </>
          )}
        </div>
      )}

      {/* Intro pages: mark complete and continue button */}
      {!isIntro && !isRef && !isCapstone && !quiz && (
        <div className="lesson__mark-complete">
          <button className="btn-mark-complete" onClick={handleMarkComplete}>
            Mark as complete and continue →
          </button>
        </div>
      )}

      {!isIntro && !isRef && !isCapstone && quiz && (
        <QuizEngine key={slug} quiz={quiz} onPass={handlePass} />
      )}

      {/* Standard prev/next nav — suppressed on capstone */}
      {!isCapstone && (
        <div className="lesson__nav">
          {adjacent.prev
            ? <Link to={`/learn/${adjacent.prev.slug}`} className="lesson__nav-back">
                <ChevronLeft size={15} />{adjacent.prev.title.slice(0, 45)}{adjacent.prev.title.length > 45 ? '…' : ''}
              </Link>
            : <Link to="/learn" className="lesson__nav-back"><ChevronLeft size={15} />Back to course</Link>
          }
          {adjacent.next && (completed || isIntro) && (
            <div className="lesson__nav-next">
              <span className="lesson__nav-next-label mono">Up next</span>
              <Link to={`/learn/${adjacent.next.slug}`} className="lesson__nav-next-link">
                {adjacent.next.title.slice(0, 45)}{adjacent.next.title.length > 45 ? '…' : ''}
                <ChevronRight size={15} />
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Capstone: simple back link only, no "up next" */}
      {isCapstone && (
        <div className="lesson__nav">
          <Link to="/learn" className="lesson__nav-back">
            <ChevronLeft size={15} />Back to course
          </Link>
        </div>
      )}
    </article>
    </CapstoneDPContext.Provider>
  )
}

// ─── Section parser ───────────────────────────────────────────────────────────
// Splits body into typed sections: markdown | callout | scenario | job-relevance

function parseSections(body) {
  const sections = []
  const lines = stripEmoji(body).split('\n')
  let i = 0
  let mdBuffer = []

  function flushMd() {
    if (mdBuffer.length > 0) {
      const text = mdBuffer.join('\n').trim()
      if (text) sections.push({ type: 'markdown', content: text })
      mdBuffer = []
    }
  }

  while (i < lines.length) {
    const line = lines[i]

    // ::: callout and decision blocks
    const calloutMatch = line.match(/^:::(tip|warning|mistakes|note|compare-wrong|compare-right|compare-reveal)\s*$/)
    const decisionMatch = line.match(/^:::decision\s+correct=([a-d])\s*$/)
    const treeMatch = line.match(/^:::tree\s+(infra|itsm|cloud|security|data)\s*$/)

    if (treeMatch) {
      flushMd()
      const tree = treeMatch[1]
      const contentLines = []
      i++
      while (i < lines.length && !lines[i].match(/^:::$/)) {
        contentLines.push(lines[i])
        i++
      }
      i++ // skip closing :::

      // First **Label** line becomes the tree name, rest is body prose
      let label = ''
      const body = []
      let labelFound = false
      for (const cl of contentLines) {
        if (!labelFound) {
          const m = cl.match(/^\*\*([^*]+)\*\*\s*(.*)$/)
          if (m) {
            label = m[1].trim()
            const rest = m[2].trim()
            if (rest) body.push(rest)
            labelFound = true
            continue
          }
        }
        body.push(cl)
      }

      sections.push({
        type: 'tree',
        tree,
        label,
        content: body.join('\n').trim(),
      })
      continue
    }

    if (decisionMatch) {
      flushMd()
      const correct = decisionMatch[1]
      const contentLines = []
      i++
      while (i < lines.length && !lines[i].match(/^:::$/)) {
        contentLines.push(lines[i])
        i++
      }
      i++ // skip closing :::
      
      // Extract REVEAL: line if present
      const revealIdx = contentLines.findIndex(l => l.startsWith('REVEAL:'))
      let reveal = ''
      let questionLines = contentLines
      if (revealIdx !== -1) {
        reveal = contentLines[revealIdx].replace('REVEAL:', '').trim()
        questionLines = contentLines.filter((_, idx) => idx !== revealIdx)
      }
      
      sections.push({ type: 'decision', correct, content: questionLines.join('\n').trim(), reveal })
      continue
    }
    if (calloutMatch) {
      flushMd()
      const ctype = calloutMatch[1]
      const contentLines = []
      i++
      while (i < lines.length && !lines[i].match(/^:::$/)) {
        contentLines.push(lines[i])
        i++
      }
      i++ // skip closing :::

      // Compare blocks — batch consecutive wrong/right/reveal into one compare section
      if (ctype === 'compare-wrong') {
        const wrongContent = contentLines.join('\n').trim()
        let rightContent = ''
        let revealContent = ''

        // Skip blanks, look for compare-right
        while (i < lines.length && !lines[i].trim()) i++
        if (i < lines.length && lines[i].match(/^:::compare-right\s*$/)) {
          i++
          const rightLines = []
          while (i < lines.length && !lines[i].match(/^:::$/)) {
            rightLines.push(lines[i]); i++
          }
          i++ // skip closing :::
          rightContent = rightLines.join('\n').trim()
        }

        // Skip blanks, look for compare-reveal
        while (i < lines.length && !lines[i].trim()) i++
        if (i < lines.length && lines[i].match(/^:::compare-reveal\s*$/)) {
          i++
          const revealLines = []
          while (i < lines.length && !lines[i].match(/^:::$/)) {
            revealLines.push(lines[i]); i++
          }
          i++ // skip closing :::
          revealContent = revealLines.join('\n').trim()
        }

        sections.push({ type: 'compare', wrong: wrongContent, right: rightContent, reveal: revealContent })
        continue
      }

      // Orphaned compare-right or compare-reveal (shouldn't happen but handle gracefully)
      if (ctype === 'compare-right' || ctype === 'compare-reveal') {
        sections.push({ type: 'callout', calloutType: 'note', content: contentLines.join('\n').trim() })
        continue
      }

      sections.push({ type: 'callout', calloutType: ctype, content: contentLines.join('\n').trim() })
      continue
    }

    // Blockquote callouts — handles all variants used across IT Foundation, HS, and AI lessons:
    //   > **NOTE**  > **WARNING**  > **TIP**
    //   > **⚠️ Watch out for these**  > **💡 Things worth knowing**
    const bqCalloutMatch = line.match(/^>\s*\*\*([^*]+)\*\*\s*$/)
    const bqCalloutType = bqCalloutMatch ? (() => {
      const raw = bqCalloutMatch[1].replace(/[\u{1F300}-\u{1FFFF}\u{2600}-\u{27BF}]/gu, '').trim().toLowerCase()
      if (raw === 'note' || raw === 'example')                    return 'note'
      if (raw === 'tip' || raw.includes('worth knowing') || raw.includes('things worth')) return 'tip'
      if (raw === 'warning')                                                              return 'warning'
      if (raw === 'mistakes' || raw.includes('watch out'))                                  return 'mistakes'
      return null // not a callout header — let it fall through
    })() : null
    if (bqCalloutMatch && bqCalloutType) {
      flushMd()
      const ctype = bqCalloutType
      const contentLines = []
      i++
      // Collect all lines belonging to this blockquote block
      while (i < lines.length) {
        const l = lines[i]
        // Stop at blank line not preceded by a blockquote line,
        // or at a non-blockquote non-blank line
        if (l.trim() === '') {
          // Peek ahead — if next non-blank line is still a blockquote, keep going
          let j = i + 1
          while (j < lines.length && lines[j].trim() === '') j++
          if (j < lines.length && lines[j].startsWith('>')) {
            i = j
            continue
          }
          break
        }
        if (!l.startsWith('>')) break
        const stripped = l.replace(/^>\s?/, '').trim()
        if (stripped) contentLines.push(stripped)
        i++
      }
      const bodyText = contentLines.join('\n').trim()
      if (bodyText) sections.push({ type: 'callout', calloutType: ctype, content: bodyText })
      continue
    }

    // Job relevance section — h2 + following paragraph
    if (line.match(/^##\s+job relevance/i)) {
      flushMd()
      sections.push({ type: 'job-relevance-heading' })
      i++
      // Skip blank lines between h2 and paragraph
      while (i < lines.length && lines[i].trim() === '') i++
      const paraLines = []
      while (i < lines.length && !lines[i].startsWith('#') && !lines[i].startsWith('---') && !lines[i].startsWith(':::')) {
        paraLines.push(lines[i])
        i++
      }
      const content = paraLines.join(' ').replace(/\s+/g, ' ').trim()
      if (content) sections.push({ type: 'job-relevance', content })
      continue
    }

    // Scenario section — detect by h2 heading
    if (line.match(/^##\s+real.world scenario/i)) {
      flushMd()
      // Extract subtitle if present (## Real-world scenario — subtitle)
      const scenarioTitle = line.replace(/^##\s+/i, '').trim()
      sections.push({ type: 'markdown', content: `## ${scenarioTitle}` })
      i++

      // Collect until next h2, ::: block (except compare), or ---
      const bqLines = []
      while (i < lines.length) {
        const l = lines[i]
        if (l.match(/^##\s/)) break
        if (l.match(/^:::(tip|warning|mistakes|note|decision)\s*$/)) break
        if (l.match(/^:::compare-wrong\s*$/)) break
        if (l.match(/^---$/) && !bqLines.some(b => b.match(/^:::compare/))) break
        bqLines.push(l.startsWith('>') ? l.replace(/^>\s?/, '') : l)
        i++
      }

      const text = bqLines.join('\n')
      const hasUser = /\*\*User[\s(:*]|\*\*[A-Z][a-zA-Z ]+\*\*:/.test(text)
      const hasYou  = /\*\*You[\s(:*]/.test(text)
      const hasCompare = /^:::compare-wrong/m.test(text)

      if (hasUser && hasYou && !hasCompare) {
        sections.push({ type: 'scenario', lines: bqLines })
      } else if (hasCompare) {
        // Sub-parse: split situation text from compare blocks
        const situationLines = []
        let j = 0
        while (j < bqLines.length && !bqLines[j].match(/^:::compare-wrong/)) {
          situationLines.push(bqLines[j])
          j++
        }
        const situationText = situationLines.join('\n').trim()
        if (situationText) {
          sections.push({ type: 'narrative-scenario', content: situationText })
        }
        // Now parse the compare blocks from bqLines[j..]
        while (j < bqLines.length) {
          const cl = bqLines[j]
          if (cl.match(/^:::compare-wrong\s*$/)) {
            j++
            const wrongLines = []
            while (j < bqLines.length && !bqLines[j].match(/^:::$/)) {
              wrongLines.push(bqLines[j]); j++
            }
            j++ // skip :::
            while (j < bqLines.length && !bqLines[j].trim()) j++
            const rightLines = []
            if (j < bqLines.length && bqLines[j].match(/^:::compare-right\s*$/)) {
              j++
              while (j < bqLines.length && !bqLines[j].match(/^:::$/)) {
                rightLines.push(bqLines[j]); j++
              }
              j++
            }
            while (j < bqLines.length && !bqLines[j].trim()) j++
            const revealLines = []
            if (j < bqLines.length && bqLines[j].match(/^:::compare-reveal\s*$/)) {
              j++
              while (j < bqLines.length && !bqLines[j].match(/^:::$/)) {
                revealLines.push(bqLines[j]); j++
              }
              j++
            }
            sections.push({
              type: 'compare',
              wrong: wrongLines.join('\n').trim(),
              right: rightLines.join('\n').trim(),
              reveal: revealLines.join('\n').trim()
            })
          } else {
            j++
          }
        }
      } else {
        sections.push({ type: 'narrative-scenario', content: text.trim() })
      }
      continue
    }

    // Suppress Quiz h2
    if (line.match(/^##\s+quiz\s*$/i)) {
      flushMd()
      i++
      continue
    }

    // Rename Job relevance h2
    if (line.match(/^##\s+why all of this matters/i)) {
      flushMd()
      sections.push({ type: 'markdown', content: '## Why all of this matters' })
      i++
      continue
    }

    mdBuffer.push(line)
    i++
  }

  flushMd()
  // Annotate each decision section with its ordinal position (0-based)
  // so DecisionBlock has a stable ID it can report to capstone context.
  let dpCounter = 0
  for (const s of sections) {
    if (s.type === 'decision') {
      s.dpIndex = dpCounter++
    }
  }
  return sections
}

// ─── Section renderer ─────────────────────────────────────────────────────────

function SectionRenderer({ section }) {
  switch (section.type) {
    case 'markdown':
      return <MdBlock content={section.content} />

    case 'compare':
      return <CompareBlock section={section} />

    case 'callout':
      if (section.calloutType === 'compare-wrong' ||
          section.calloutType === 'compare-right' ||
          section.calloutType === 'compare-reveal') {
        // These are batched into CompareBlock by the section collector below
        // If rendered individually (shouldn't happen), fall through to note
        return null
      }
      return (
        <div className={`callout callout--${section.calloutType}`}>
          <span className="callout__label">{calloutLabel(section.calloutType)}</span>
          <div className="callout__body">
            <MdBlock content={section.content} />
          </div>
        </div>
      )

    case 'narrative-scenario':
      return (
        <div className="scenario scenario--narrative">
          <div className="scenario__header">
            <span className="scenario__dot" />
            <span className="scenario__tag mono">Real-world scenario</span>
          </div>
          <div className="scenario__narrative">
            <MdBlock content={section.content} />
          </div>
        </div>
      )

    case 'job-relevance-heading':
      return <h2 className="lesson__h2">Why all of this matters</h2>

    case 'job-relevance':
      return (
        <div className="job-relevance">
          <span className="job-relevance__label">On the job</span>
          <p>{section.content}</p>
        </div>
      )

    case 'decision':
      return <DecisionBlock section={section} />

    case 'scenario':
      return <ScenarioBlock lines={section.lines} />

    case 'tree':
      return <TreeBlock section={section} />

    default:
      return null
  }
}

// ─── Tree block ───────────────────────────────────────────────────────────────
// Colour-coded career tree card. Used in the capstone's "What comes next"
// section. Palette mirrors learn-more.html and TreeSelection.

function TreeBlock({ section }) {
  const { tree, label, content } = section
  return (
    <div className={`tree-card tree-card--${tree}`}>
      <div className="tree-card__head">
        <span className="tree-card__name">{label}</span>
      </div>
      <div className="tree-card__body">
        <MdBlock content={content} />
      </div>
    </div>
  )
}

// ─── Capstone render ──────────────────────────────────────────────────────────
// Custom render for the Foundation Capstone. The structural chrome is
// hardcoded JSX (this is a one-off ceremonial page, not a recurring content
// type — putting all this in markdown convention would mean inventing 8+
// new block types only used once). The 3 decision points are extracted
// from the parsed sections so they remain editable in the markdown file.

function CapstoneRender({ sections }) {
  // Extract the three decision sections from the parsed body. Order matters:
  // sections come back in document order, so the first decision is DP1.
  const decisions = sections.filter(s => s.type === 'decision')
  const [dp1, dp2, dp3] = decisions

  return (
    <div className="cap">

      {/* ── Hero ── */}
      <div className="cap-hero">
        <div className="cap-hero__eyebrow mono">Foundation Capstone</div>
        <h1 className="cap-hero__title">Getting real.</h1>
        <p className="cap-hero__sub">You've built the foundation. This is what it looks like when you use it.</p>
        <div className="cap-hero__meta mono">
          <span>25–30 min</span>
          <span>All tracks</span>
          <span>Synthesis</span>
        </div>
      </div>

      {/* ── Intro ── */}
      <div className="cap-intro">
        <p className="cap-prose">You've completed all three tracks. That means something.</p>
        <p className="cap-prose">Not everyone gets here — most people who start a course finish some of it. You've finished all of it: the technical foundation, the human skills track, and the AI foundations track. Three different registers. Three different kinds of knowledge. All of them pointing at the same thing: what it actually takes to function as an IT professional in environments where technical competence, professional judgment, and AI literacy are all required simultaneously.</p>
        <p className="cap-prose">Work through the scenario. There are decision points along the way — they're not tests, they're the thinking made visible.</p>
      </div>

      {/* ── Scenario setup ── */}
      <h2 className="cap-sec-heading">The scenario</h2>
      <p className="cap-prose">It's 8:47 on a Monday morning. You're seventeen days into your first IT job — service desk, mid-size company, about 400 users across three floors and a remote workforce.</p>
      <p className="cap-prose">Your team lead is already in a meeting. The other first-line analyst called in sick. You are, for the next two hours at least, the person.</p>
      <p className="cap-prose">The phone rings.</p>

      {/* ── Calls log ── */}
      <div className="cap-calls-header">
        <div className="cap-calls-header__title">Two calls</div>
        <div className="cap-calls-header__day mono">Monday · 08:47 → 09:03</div>
      </div>

      {/* Call 1 — Janet */}
      <div className="cap-call">
        <div className="cap-call__marker" />
        <div className="cap-call__header">
          <span className="cap-call__time mono">08:47</span>
          <span className="cap-call__who">Janet · PA to the MD</span>
          <span className="cap-call__duration mono">~5 min</span>
        </div>
        <p className="cap-prose">She says her name and that "the email system is down." Her voice is calm but clipped. She's been in this building longer than the IT department has existed and she does not have time for this.</p>
        <p className="cap-prose">You check the monitoring dashboard. Email is showing green. No alerts. No tickets open for email in the last hour.</p>

        {dp1 && <DecisionBlock section={dp1} />}

        <p className="cap-prose">She's getting an error. You ask her to read it to you. <em>0x800CCC0F. Connection to the server was interrupted.</em></p>
        <p className="cap-prose">You know this one. Network connectivity — Outlook can reach the internet but not the mail server specifically. You check her machine in the RMM tool. She's connected to the guest Wi-Fi instead of the corporate network. Probably switched automatically when she moved across the building.</p>

        {dp2 && <DecisionBlock section={dp2} />}

        <p className="cap-prose">You walk her through switching networks in three steps, using the words she'll see on screen. Outlook opens. Email loads. "Thank you," she says. She hangs up.</p>
      </div>

      {/* Call 2 — Marcus */}
      <div className="cap-call">
        <div className="cap-call__marker" />
        <div className="cap-call__header">
          <span className="cap-call__time mono">08:54</span>
          <span className="cap-call__who">Marcus · Third floor</span>
          <span className="cap-call__duration mono">~5 min</span>
        </div>
        <p className="cap-prose">He sounds genuinely stressed — not aggressive, just the voice of someone who has been fighting with something for longer than he should have before calling.</p>
        <p className="cap-prose">His laptop won't connect to the VPN. He works remotely three days a week. Today he's come into the office for a meeting — his presentation starts in forty minutes. You ask him what the error says. <em>Authentication failure.</em> You check his account. His password expired at midnight.</p>

        {dp3 && <DecisionBlock section={dp3} />}

        <p className="cap-prose">You walk him through the reset one step at a time, waiting for his confirmation before moving on. When the VPN connects, the tension leaves his voice almost audibly. "That's brilliant, thank you — I've been staring at this for twenty minutes."</p>
        <p className="cap-prose">You flag the password expiry notification system — remote workers hitting this wall is going to happen again.</p>
        <p className="cap-prose">The queue is clear. It's 09:03.</p>
      </div>

      {/* ── The moment ── */}
      <div className="cap-moment">
        <div className="cap-moment__eyebrow mono">What just happened</div>
        <div className="cap-moment__stats">
          <div>
            <div className="cap-moment__num">2</div>
            <div className="cap-moment__label mono">Calls</div>
          </div>
          <div>
            <div className="cap-moment__num">16</div>
            <div className="cap-moment__label mono">Minutes</div>
          </div>
          <div>
            <div className="cap-moment__num">0</div>
            <div className="cap-moment__label mono">Escalations</div>
          </div>
          <div>
            <div className="cap-moment__num">0</div>
            <div className="cap-moment__label mono">Callbacks</div>
          </div>
        </div>
        <p className="cap-moment__prose">Neither call was technically complex. Both of them required everything Foundation has built.</p>
      </div>

      {/* ── What you used (competency tags) ── */}
      <div className="cap-used">
        <div className="cap-sec-eyebrow mono">What each call actually required</div>
        <h2 className="cap-sec-heading">Everything, simultaneously.</h2>

        <div className="cap-used__row">
          <div className="cap-used__who mono">Janet</div>
          <div className="cap-used__tags">
            <span className="cap-used__tag">Network fundamentals</span>
            <span className="cap-used__tag">Error code recognition</span>
            <span className="cap-used__tag">RMM tooling</span>
            <span className="cap-used__tag">Audience awareness</span>
            <span className="cap-used__tag">Tone calibration</span>
            <span className="cap-used__tag">Headline-first comms</span>
            <span className="cap-used__tag">Reading the room</span>
            <span className="cap-used__tag">Organisational context</span>
          </div>
        </div>

        <div className="cap-used__row">
          <div className="cap-used__who mono">Marcus</div>
          <div className="cap-used__tags">
            <span className="cap-used__tag">Authentication concepts</span>
            <span className="cap-used__tag">VPN fundamentals</span>
            <span className="cap-used__tag">Account management</span>
            <span className="cap-used__tag">Active listening</span>
            <span className="cap-used__tag">Comms under pressure</span>
            <span className="cap-used__tag">Matching pace to stress</span>
            <span className="cap-used__tag">Flagging a process gap</span>
          </div>
        </div>
      </div>

      {/* ── AI note ── */}
      <div className="cap-ai-note">
        <div className="cap-ai-note__label mono">The third dimension</div>
        <p>Neither call made this obvious but both quietly involved it. Every tool you used has AI embedded in it. The monitoring dashboard showing green wasn't just a threshold check — it was ML-based anomaly detection. The ticket system's auto-categorisation made a call about where Marcus's ticket should go before you saw it. <strong>You worked alongside those systems without thinking about them specifically.</strong> That's what it looks like when the AI literacy is working — not dramatic, not visible, just present in the background of every decision about what to trust and what to verify yourself.</p>
      </div>

      {/* ── The thesis statement ── */}
      <div className="cap-statement">
        <p className="cap-statement__text">This is what IT work actually looks like. <em>Not one skill at a time. All of them, simultaneously, with a real person on the other end of the call.</em></p>
      </div>

      {/* ── Three pillars ── */}
      <div className="cap-pillars">
        <div className="cap-pillars__intro">
          <div className="cap-pillars__eyebrow mono">What you're leaving with</div>
          <h2 className="cap-pillars__title">Three things you didn't have before.</h2>
        </div>
        <div className="cap-pillars__list">
          <div className="cap-pillar">
            <div className="cap-pillar__num mono">01</div>
            <div className="cap-pillar__body">
              <div className="cap-pillar__title">A mental model of IT infrastructure.</div>
              <div className="cap-pillar__desc">Not every detail — Foundation was never going to give you every detail — but the shape of it. How hardware connects to networks. How networks connect to services. How operating systems sit on top of hardware and how users sit on top of everything. How things break and why, and what a systematic approach to fixing them looks like.</div>
            </div>
          </div>
          <div className="cap-pillar">
            <div className="cap-pillar__num mono">02</div>
            <div className="cap-pillar__body">
              <div className="cap-pillar__title">A professional communication toolkit.</div>
              <div className="cap-pillar__desc">You know what it means to write for the reader rather than yourself. You know the difference between waiting and listening. You know how to handle a situation where you don't have the answer. You know that <strong>tone lands before words do</strong>, and that reading the room is a skill, not a personality trait.</div>
            </div>
          </div>
          <div className="cap-pillar">
            <div className="cap-pillar__num mono">03</div>
            <div className="cap-pillar__body">
              <div className="cap-pillar__title">A working relationship with AI.</div>
              <div className="cap-pillar__desc">Not credulous, not dismissive — calibrated. You know when to trust it and when to override it. You know what never goes into an AI tool. You know what accountability means when you've shipped something wrong. You know what the technology actually is, not what the hype says it is.</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Honest paragraph ── */}
      <div className="cap-honest">
        <div className="cap-honest__label mono">Be honest with yourself</div>
        <p>Foundation is a starting point. There are IT professionals with fifteen years of experience who know things you don't yet know. There are systems you haven't touched and situations that will catch you off guard in your first role regardless of how well you prepared.</p>
        <p>That's fine. That's how it works. Foundation hasn't made you an expert — it's made you someone who is ready to start becoming one. There's a meaningful difference, and it matters.</p>
      </div>

      {/* ── Trees ── */}
      <div className="cap-trees-section">
        <div className="cap-trees-section__header">
          <div className="cap-trees-section__eyebrow mono">Tier 2 — where specialisation begins</div>
          <h2 className="cap-trees-section__title">Five trees. One next step.</h2>
          <p className="cap-trees-section__sub">The content gets deeper, the scenarios harder, the skills start compounding. Here's what each path actually looks like when you're inside it.</p>
        </div>

        <div className="cap-trees-grid">
          <CapTree tree="infra" name="Infrastructure" tag="Systems" pitch="From understanding networks to building and managing them.">
            Active Directory, server administration, advanced networking, virtualisation at scale. The calls stop being "my email isn't working" and start being "we've lost connectivity to the secondary site." Less user-facing, more systems-facing.
            <div className="cap-tree__signal mono"><strong>Right for you if:</strong> you want to understand IT from the ground up.</div>
          </CapTree>

          <CapTree tree="itsm" name="ITSM" tag="Service" pitch="From understanding the helpdesk to running it.">
            ITIL, ServiceNow, Jira, change management, incident ownership. You stop being the person who handles tickets and start being the person who designs the system that handles them.
            <div className="cap-tree__signal mono"><strong>Right for you if:</strong> the organisational side of IT interests you as much as the technical.</div>
          </CapTree>

          <CapTree tree="cloud" name="Cloud" tag="Platforms" pitch="Infrastructure as configuration rather than hardware.">
            Azure, AWS, cloud architecture, deployment pipelines. Tier 2 Cloud builds toward understanding how modern IT systems actually live.
            <div className="cap-tree__signal mono"><strong>Right for you if:</strong> you like the idea of infrastructure that scales at a click, not a rack.</div>
          </CapTree>

          <CapTree tree="security" name="Security" tag="Defense" pitch="From knowing threats exist to knowing how they work.">
            Network security, vulnerability management, threat landscape, the beginning of SOC analyst skills. You think about IT from the adversary's perspective.
            <div className="cap-tree__signal mono"><strong>Right for you if:</strong> you're more interested in how something could be broken than how it was built.</div>
          </CapTree>

          <CapTree tree="data" name="Data & Analytics" tag="Insight" pitch="IT systems generate data. You make it mean something.">
            Databases, reporting, business intelligence, analytics platforms. The output isn't uptime or a closed ticket — it's insight that drives decisions.
            <div className="cap-tree__signal mono"><strong>Right for you if:</strong> you're drawn to patterns, to what the numbers are actually saying.</div>
          </CapTree>
        </div>
      </div>

      {/* ── Closing line ── */}
      <div className="cap-closing">
        <div className="cap-closing__small mono">One more thing</div>
        <div className="cap-closing__line">We're just getting started.</div>
      </div>

    </div>
  )
}

// Helper component for capstone tree cards (no coloured dot per Getoar's note)
function CapTree({ tree, name, tag, pitch, children }) {
  return (
    <div className={`cap-tree cap-tree--${tree}`}>
      <div className="cap-tree__head">
        <span className="cap-tree__name">{name}</span>
        <span className="cap-tree__tag mono">{tag}</span>
      </div>
      <div className="cap-tree__body">
        <div className="cap-tree__pitch">{pitch}</div>
        {children}
      </div>
    </div>
  )
}

// ─── Markdown block ───────────────────────────────────────────────────────────

// Safely extract text from React children without stringifying elements
function extractChildText(children) {
  if (!children) return ''
  if (typeof children === 'string') return children
  if (Array.isArray(children)) return children.map(extractChildText).join('')
  if (React.isValidElement(children)) return extractChildText(children.props?.children)
  return ''
}

function MdBlock({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h2: ({ children }) => {
          const clean = stripEmoji(extractChildText(children)).trim()
          if (clean.toLowerCase() === 'quiz') return null
          return <h2 className="lesson__h2">{children}</h2>
        },
        h3: ({ children }) => <h3 className="lesson__h3">{children}</h3>,
        hr: () => null,
        p:  ({ children }) => <p>{children}</p>,
        table: ({ children }) => (
          <div style={{ overflowX: 'auto', margin: '1.4em 0', borderRadius: '10px', border: '1px solid var(--border-2)' }}>
            <table>{children}</table>
          </div>
        ),
        code: ({ node, children, className, ...props }) => {
          const isBlock = className && className.startsWith('language-')
          if (isBlock) {
            const lang = className.replace('language-', '')
            return (
              <pre className={`code-block code-block--lang code-block--${lang}`}>
                {lang && <span className="code-block__lang">{lang}</span>}
                <code className={className} {...props}>{children}</code>
              </pre>
            )
          }
          // Unlabelled fenced blocks: children will be a string with newlines
          // Inline code: children is a simple string with no newlines
          const text = typeof children === 'string' ? children : ''
          if (text.includes('\n')) {
            // Check if this is a command table (lines with — or # as separator)
            const lines = text.split('\n').filter(l => l.trim())
            const isCommandTable = lines.length > 1 && lines.every(l =>
              l.includes('  —  ') || l.includes('  # ') || l.match(/\s{2,}—\s|\s{2,}#\s/)
            )
            if (isCommandTable) {
              return <CommandTable lines={lines} />
            }
            return (
              <pre className="code-block">
                <code {...props}>{children}</code>
              </pre>
            )
          }
          // Nav path detection: inline code containing → arrows
          if (text.includes('→')) {
            return <NavPath path={text} />
          }
          return <code className="inline-code" {...props}>{children}</code>
        },
        // Do NOT stringify children — they may be React elements (inline code nodes)
        strong: ({ children }) => <strong>{children}</strong>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
        ),
        blockquote: ({ children }) => {
          // Detect callout type from first bold child text
          // AI lessons use > **TIP** / > **WARNING** / > **NOTE** / > **⚠️...** / > **💡...**
          let calloutType = 'note'
          let label = 'Note'

          // Walk children to find the first strong/bold text
          const findFirstStrong = (nodes) => {
            if (!nodes) return null
            for (const node of (Array.isArray(nodes) ? nodes : [nodes])) {
              if (!node) continue
              if (node.type === 'strong' || (node.props && node.props.children)) {
                const text = typeof node.props?.children === 'string'
                  ? node.props.children
                  : Array.isArray(node.props?.children)
                    ? node.props.children.map(c => typeof c === 'string' ? c : '').join('')
                    : ''
                if (text) return text.trim()
              }
              if (node.props?.children) {
                const found = findFirstStrong(node.props.children)
                if (found) return found
              }
            }
            return null
          }

          const firstBold = findFirstStrong(Array.isArray(children) ? children : [children]) || ''
          const upper = firstBold.toUpperCase()

          if (upper.startsWith('TIP') || firstBold.startsWith('💡')) {
            calloutType = 'tip'; label = 'Tip'
          } else if (upper.startsWith('WARNING') || firstBold.startsWith('⚠')) {
            calloutType = 'warning'; label = 'Warning'
          } else if (upper.startsWith('WATCH OUT') || upper.startsWith('COMMON MISTAKE')) {
            calloutType = 'mistakes'; label = 'Watch out'
          } else if (upper.startsWith('NOTE') || upper.startsWith('AI FOUNDATION') || upper.startsWith('IT FOUNDATION') || upper.startsWith('HUMAN SKILLS')) {
            calloutType = 'note'; label = 'Note'
          }

          return (
            <div className={`callout callout--${calloutType}`}>
              <span className="callout__label">{label}</span>
              <div className="callout__body">{children}</div>
            </div>
          )
        },
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

// ─── Decision block ───────────────────────────────────────────────────────────

function DecisionBlock({ section }) {
  const [picked, setPicked] = React.useState(null)
  const { correct, content, reveal, dpIndex } = section

  // If this decision block is rendered inside a capstone, it reports its
  // answered state upward so the capstone CTA can enable itself. Outside a
  // capstone (normal lessons), context is null and reporting is a no-op.
  const capstoneCtx = useContext(CapstoneDPContext)

  // Parse question and options from content
  const lines = content.split('\n')
  const optionLines = lines.filter(l => l.match(/^\*\*[A-D]\)/))
  const questionLines = lines.filter(l => !l.match(/^\*\*[A-D]\)/) && l.trim())
  const question = questionLines.join(' ').trim()

  const options = optionLines.map(l => {
    const m = l.match(/^\*\*([A-D])\)\*\*\s*(.+)/)
    return m ? { key: m[1].toLowerCase(), text: m[2].trim() } : null
  }).filter(Boolean)

  function handlePick(key) {
    if (picked) return
    setPicked(key)
    // Report this DP as answered so the capstone CTA can enable.
    // Note: we don't gate this on whether the capstone is "already earned" —
    // the DPs are educational content and always interactive. The only thing
    // that changes when the capstone is already complete is the CTA button
    // at the bottom of the page (which shows "View certificate" instead of
    // "Complete Foundation").
    if (capstoneCtx?.reportDPAnswered && typeof dpIndex === 'number') {
      capstoneCtx.reportDPAnswered(dpIndex)
    }
  }

  const answered = !!picked

  return (
    <div className="dp">
      <div className="dp-q">
        <span className="dp-label mono">Decision point</span>
        {question}
      </div>
      <div className="dp-opts">
        {options.map(opt => {
          const isSelected = picked === opt.key
          const isCorrect  = opt.key === correct
          let cls = 'dp-opt'
          if (answered) {
            if (isSelected && isCorrect)  cls += ' dp-opt--correct'
            else if (isSelected)          cls += ' dp-opt--wrong'
            else if (isCorrect)           cls += ' dp-opt--correct-answer'
            else                          cls += ' dp-opt--dim'
          }
          return (
            <button
              key={opt.key}
              className={cls}
              onClick={() => handlePick(opt.key)}
              disabled={answered}
            >
              <span className="dp-key">{opt.key.toUpperCase()}</span>
              <span className="dp-text">{opt.text}</span>
              {answered && isSelected && (
                <span className="dp-badge">{isCorrect ? 'Correct' : 'Wrong'}</span>
              )}
              {answered && !isSelected && isCorrect && (
                <span className="dp-badge dp-badge--answer">Correct answer</span>
              )}
            </button>
          )
        })}
      </div>
      {answered && reveal && (
        <div className="dp-reveal">
          <span className="dp-reveal-arrow">→ {correct.toUpperCase()}</span>
          <span className="dp-reveal-text" dangerouslySetInnerHTML={{ __html: reveal.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
        </div>
      )}
    </div>
  )
}

// ─── Compare block (HS lessons — interactive A vs B) ─────────────────────────

function isDialogueContent(content) {
  return /^\*(?:User|IT|Candidate|Interviewer|Manager|Engineer)[^*]*:/m.test(content)
}

function DialogueLines({ content }) {
  const lines = content.split('\n').filter(l => l.trim())
  return (
    <div className="cmp-dialogue">
      {lines.map((line, i) => {
        const m = line.match(/^\*([^:]+):\s*"?(.+?)"?\*?$/)
        if (m) {
          const who = m[1].trim()
          const text = m[2].trim().replace(/^"|"$/g, '')
          const isIT = /^IT|^You|^Engineer B/i.test(who)
          return (
            <div key={i} className="cmp-dl">
              <span className={`cmp-dl__who ${isIT ? 'cmp-dl__who--it' : 'cmp-dl__who--user'}`}>{who}</span>
              <span className="cmp-dl__text">"{text}"</span>
            </div>
          )
        }
        return line.trim() ? <p key={i} className="cmp-dl__plain">{line.replace(/^\*|\*$/g, '')}</p> : null
      })}
    </div>
  )
}

function CompareBlock({ section }) {
  const [picked, setPicked] = React.useState(null)
  const { wrong, right, reveal } = section
  const answered = !!picked

  function pick(option) {
    if (answered) return
    setPicked(option)
  }

  const wrongIsDialogue = isDialogueContent(wrong)
  const rightIsDialogue = isDialogueContent(right)

  // Extract label from content (first **bold** line = Engineer A / Version A etc.)
  function getLabel(content, fallback) {
    const m = content.match(/^\*\*([^*]+)\*\*/)
    return m ? m[1].trim() : fallback
  }
  function stripLabel(content) {
    let s = content.replace(/^\*\*[^*]+\*\*\n\n?/, '').trim()
    // Strip leading comma+space (e.g. ", when asked...")
    s = s.replace(/^,\s*/, '')
    // Capitalise first letter
    if (s.length > 0) s = s.charAt(0).toUpperCase() + s.slice(1)
    return s
  }

  const labelA = getLabel(wrong, 'Option A')
  const labelB = getLabel(right, 'Option B')
  const bodyA  = stripLabel(wrong)
  const bodyB  = stripLabel(right)

  return (
    <div className="cmp">
      <div className="cmp__prompt mono">Which approach is right?</div>
      <div className="cmp__cards">

        {/* Option A — wrong */}
        <div
          className={`cmp-card ${answered ? (picked === 'a' ? 'cmp-card--wrong' : 'cmp-card--dim') : ''}`}
          onClick={() => pick('a')}
        >
          <div className="cmp-card__header">
            <span className="cmp-card__label">{labelA}</span>
            {answered && picked === 'a' && <span className="cmp-card__badge cmp-card__badge--wrong">Wrong</span>}
          </div>
          <div className="cmp-card__body">
            {wrongIsDialogue ? <DialogueLines content={bodyA} /> : <MdBlock content={bodyA} />}
          </div>
        </div>

        {/* Option B — right */}
        <div
          className={`cmp-card ${answered ? (picked === 'b' ? 'cmp-card--correct' : (picked === 'a' ? 'cmp-card--correct cmp-card--dim' : '')) : ''}`}
          onClick={() => pick('b')}
        >
          <div className="cmp-card__header">
            <span className="cmp-card__label">{labelB}</span>
            {answered && <span className="cmp-card__badge cmp-card__badge--correct">Correct</span>}
          </div>
          <div className="cmp-card__body">
            {rightIsDialogue ? <DialogueLines content={bodyB} /> : <MdBlock content={bodyB} />}
          </div>
        </div>

      </div>

      {answered && reveal && (
        <div className="cmp__reveal">
          <span className="cmp__reveal-label mono">Why</span>
          <div className="cmp__reveal-text">
            {(() => {
              const correctMatch = reveal.match(/^CORRECT:\s*(.+?)(?=\nA:|\n*$)/s)
              const aMatch = reveal.match(/\nA:\s*(.+?)(?=\nB:|\n*$)/s)
              const bMatch = reveal.match(/\nB:\s*(.+?)$/s)
              if (correctMatch && aMatch && bMatch) {
                return (
                  <>
                    <p className="cmp__reveal-correct">{correctMatch[1].trim()}</p>
                    <div className="cmp__reveal-outcomes">
                      <div className="cmp__reveal-outcome cmp__reveal-outcome--a">
                        <span className="cmp__reveal-outcome-label">{labelA}</span>
                        <span>{aMatch[1].trim()}</span>
                      </div>
                      <div className="cmp__reveal-outcome cmp__reveal-outcome--b">
                        <span className="cmp__reveal-outcome-label">{labelB}</span>
                        <span>{bMatch[1].trim()}</span>
                      </div>
                    </div>
                  </>
                )
              }
              return <MdBlock content={reveal} />
            })()}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Command table ─────────────────────────────────────────────────────────────
// Converts command blocks with — or # separators into a clean two-column table

function CommandTable({ lines }) {
  const rows = lines.map(line => {
    // Split on '  —  ' or '  # ' or similar spacing patterns
    const sepMatch = line.match(/^(.+?)\s{2,}[—#]\s+(.+)$/)
    if (sepMatch) {
      return { cmd: sepMatch[1].trim(), desc: sepMatch[2].trim() }
    }
    // Single command line with no description
    return { cmd: line.trim(), desc: '' }
  })

  return (
    <div className="cmd-table-wrap">
      <table className="cmd-table">
        <thead>
          <tr>
            <th>Command</th>
            <th>What it does</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td><code className="inline-code">{row.cmd}</code></td>
              <td>{row.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Nav path chips ───────────────────────────────────────────────────────────
// Renders navigation paths like Settings → System → Recovery as wrapping chips

function NavPath({ path }) {
  const steps = path.split(/\s*→\s*/).map(s => s.trim()).filter(Boolean)
  return (
    <div className="nav-path">
      {steps.map((step, i) => (
        <React.Fragment key={i}>
          <span className="nav-path__chip">{step}</span>
          {i < steps.length - 1 && <span className="nav-path__arrow">→</span>}
        </React.Fragment>
      ))}
    </div>
  )
}

function ScenarioBlock({ lines }) {
  const parsed = parseScenarioLines(lines)

  return (
    <div className="scenario">
      <div className="scenario__header">
        <span className="scenario__dot" />
        <span className="scenario__tag mono">Live call — helpdesk</span>
      </div>

      {parsed.situation && (
        <div className="scenario__situation">
          <span className="scenario__situation-label mono">The situation</span>
          <p>{parsed.situation}</p>
        </div>
      )}

      {parsed.chat.length > 0 && (
        <div className="scenario__chat">
          {parsed.chat.map((line, i) => (
            <div key={i} className="chat-line">
              <span className={`chat-who chat-who--${line.speaker}`}>
                {line.speaker === 'user' ? 'User' : 'You'}
              </span>
              <span className={`chat-text ${line.speaker === 'you' ? 'chat-text--you' : ''}`}>
                {line.text}
              </span>
            </div>
          ))}
        </div>
      )}

      {parsed.outcome && (
        <div className="scenario__outcome">
          <strong>What this shows: </strong>
          <em>{parsed.outcome}</em>
        </div>
      )}
    </div>
  )
}
